import React, { useCallback, useEffect } from "react";

import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useShopifyCart } from "@app/scripts/react-hooks/shopify";
import { CartFreeShipping } from "@app/scripts/react-cart/components/CartFreeShipping";
import { CartItems } from "@app/scripts/react-cart/components/CartItems";
import { CartOrderSummary } from "@app/scripts/react-cart/components/CartOrderSummary";

import type { GiftProduct } from "@app/scripts/types";

export type Props = {
  heading?: string;
  freeShipping?: number;
  giftProduct?: GiftProduct;
  youMayAlsoLikeHeading?: string;
  lowStockThreshold: number;
  youMayAlsoLikeCollection?: number[];
  youMayAlsoLikeProductsToShow: number;
};

export const CartDrawer: React.FC<Props> = ({
  heading,
  giftProduct,
  freeShipping,
  youMayAlsoLikeHeading,
  lowStockThreshold,
  youMayAlsoLikeCollection,
  youMayAlsoLikeProductsToShow,
}) => {
  const {
    products,
    cart,
    fetchCart,
    goToCheckout,
    handleUpdate,
    totalDiscount,
    addOosVariant,
    cartTotal,
    giftMessage,
    giftsInCart,
    overGiftThreshold,
    untilGiftThreshold,
    giftIds,
  } = useShopifyCart({ giftProduct });

  useEffect(() => {
    window.addEventListener("cartAdd", fetchCart);
    fetchCart();

    return () => {
      window.removeEventListener("cartAdd", fetchCart);
    };
  }, []);

  return !closed ? (
    <Drawer
      drawerClassName="cart-drawer"
      heading={`${heading ?? "Your Bag"}`}
      secondaryHeading={`(${cart?.item_count} Item${cart && cart.item_count > 1 ? "s" : ""})`}
      customButton={Btn}
    >
      <CartContent
        totalDiscount={totalDiscount}
        goToCheckout={goToCheckout}
        cartTotal={cartTotal}
        freeShipping={freeShipping}
        cart={cart}
        products={products}
        handleUpdate={handleUpdate}
        addOosVariant={addOosVariant}
        giftsInCart={giftsInCart}
        overGiftThreshold={overGiftThreshold}
        untilGiftThreshold={untilGiftThreshold}
        giftIds={giftIds}
        giftMessage={giftMessage}
        lowStockThreshold={lowStockThreshold}
        youMayAlsoLikeHeading={youMayAlsoLikeHeading}
        youMayAlsoLikeCollection={youMayAlsoLikeCollection}
        youMayAlsoLikeProductsToShow={youMayAlsoLikeProductsToShow}
      />
    </Drawer>
  ) : null;
};

const CartContent = ({
  totalDiscount,
  goToCheckout,
  cartTotal,
  freeShipping,
  cart,
  toggleOpen,
  products,
  handleUpdate,
  addOosVariant,
  giftId,
  giftMessage,
  lowStockThreshold,
  youMayAlsoLikeHeading,
  youMayAlsoLikeCollection,
  youMayAlsoLikeProductsToShow,
  giftsInCart,
  overGiftThreshold,
  untilGiftThreshold,
  giftIds,
}) => {
  return (
    <div className="cart__inner">
      <CartFreeShipping cart={cart} freeShipping={freeShipping} />

      <CartItems
        cart={cart}
        products={products}
        handleUpdate={handleUpdate}
        addOosVariant={addOosVariant}
        giftId={giftId}
        giftMessage={giftMessage}
        lowStockThreshold={lowStockThreshold}
        notifyLabel="Notify Me"
        youMayAlsoLikeHeading={youMayAlsoLikeHeading}
        youMayAlsoLikeCollection={youMayAlsoLikeCollection}
        youMayAlsoLikeProductsToShow={youMayAlsoLikeProductsToShow}
        giftsInCart={giftsInCart}
        overGiftThreshold={overGiftThreshold}
        untilGiftThreshold={untilGiftThreshold}
        giftIds={giftIds}
      />

      <CartOrderSummary
        totalDiscount={totalDiscount}
        cart={cart}
        goToCheckout={goToCheckout}
        cartTotal={cartTotal}
        toggleOpen={toggleOpen}
      />
    </div>
  );
};

const Btn = ({ toggleOpen, open }) => {
  const openOnAdd = useCallback(() => {
    if (!open) toggleOpen?.();
  }, [open, toggleOpen]);

  useEffect(() => {
    const cartButtons = document.querySelectorAll("[data-togglecart]");

    cartButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        toggleOpen?.();
      });
    });

    window.addEventListener("cartAdd", openOnAdd);

    return () => {
      window.removeEventListener("cartAdd", openOnAdd);
    };
  }, []);

  return null;
};
